import { useState, useRef, useEffect, useCallback } from 'react'
import {
  X,
  Image as ImageIcon,
  FileText,
  Package,
  Download,
  ArrowLeft,
  UploadCloud,
  Trash2,
  Loader2,
  Crosshair,
  Video as VideoIcon,
  Volume2,
  Captions as CaptionsIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import type {
  WireImageArtifact,
  WireWordDocumentArtifact,
  WireVisualArtifact,
  WireVideoArtifact,
  WireCaptionsArtifact,
  WireAudioArtifact,
  WireArtifact,
  WireArticleFull,
  WireArticleThumbnail,
} from '../../api/types'
import { PortionMark } from '../article-viewer/common/PortionMark'
import { useServices } from '../../api/ServiceContext'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt/types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1_048_576).toFixed(1)} MB`
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function pl(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

// ─── Staging types ────────────────────────────────────────────────────────────

interface StagingFile {
  file: File
  localUrl: string
  type: 'image' | 'visual' | 'video' | 'captions' | 'audio'
}

interface ImageUploadMeta {
  classification: string
  disseminationControls: string[]
  altText: string
  caption: string
  captionClassification: string
  captionDisseminationControls: string[]
  credit: string
  focalPoint: { x: number; y: number }
  setAsThumbnail: boolean
}

// ─── FocalPointPicker ─────────────────────────────────────────────────────────

function FocalPointPicker({
  imageUrl,
  value,
  onChange,
}: {
  imageUrl: string
  value: { x: number; y: number }
  onChange: (p: { x: number; y: number }) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
    onChange({ x, y })
  }

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
        Focal Point{' '}
        <span className="font-normal normal-case tracking-normal opacity-60">
          — click to set
        </span>
      </p>
      <div
        ref={containerRef}
        className="relative cursor-crosshair overflow-hidden rounded border border-line"
        style={{ aspectRatio: '4/3' }}
        onClick={handleClick}
        role="button"
        aria-label={`Set focal point. Current: ${Math.round(value.x * 100)}% from left, ${Math.round(value.y * 100)}% from top`}
      >
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
          draggable={false}
        />
        {/* Focal point dot */}
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${value.x * 100}%`, top: `${value.y * 100}%` }}
        >
          <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-black/40 shadow-lg">
            <Crosshair size={12} className="text-white" aria-hidden="true" />
          </div>
        </div>
        {/* Crosshair lines */}
        <div
          className="pointer-events-none absolute inset-y-0 w-px bg-white/50"
          style={{ left: `${value.x * 100}%` }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-x-0 h-px bg-white/50"
          style={{ top: `${value.y * 100}%` }}
          aria-hidden="true"
        />
      </div>
      <p className="text-[10px] text-fg-secondary opacity-60">
        {Math.round(value.x * 100)}% × {Math.round(value.y * 100)}%
      </p>
    </div>
  )
}

// ─── Image staging form ───────────────────────────────────────────────────────

function ImageStagingForm({
  staging,
  queueLength,
  uploadPending,
  onUpload,
  onSkip,
  onCancel,
}: {
  staging: StagingFile
  queueLength: number
  uploadPending: boolean
  onUpload: (meta: ImageUploadMeta) => void
  onSkip: () => void
  onCancel: () => void
}) {
  const [cmt, setCmt] = useState<CmtResult | undefined>(undefined)
  const [altText, setAltText] = useState('')
  const [caption, setCaption] = useState('')
  const [captionCmt, setCaptionCmt] = useState<CmtResult>({ classification: 'U', disseminationControls: [] })
  const [credit, setCredit] = useState('')
  const [focalPoint, setFocalPoint] = useState<{ x: number; y: number }>({ x: 0.5, y: 0.5 })
  const [setAsThumbnail, setSetAsThumbnail] = useState(false)

  const canUpload = !uploadPending && cmt !== undefined

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!cmt || !canUpload) return
    onUpload({
      classification: cmt.classification,
      disseminationControls: cmt.disseminationControls,
      altText,
      caption,
      captionClassification: captionCmt.classification,
      captionDisseminationControls: captionCmt.disseminationControls,
      credit,
      focalPoint,
      setAsThumbnail,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      {/* Staging sub-header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-line bg-fill px-5 py-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploadPending}
          className="rounded p-1 text-fg-secondary transition-colors hover:bg-canvas hover:text-fg disabled:opacity-40"
          aria-label="Cancel upload"
        >
          <ArrowLeft size={14} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-fg" title={staging.file.name}>
            {staging.file.name}
          </p>
          <p className="text-[10px] text-fg-secondary">{fmtBytes(staging.file.size)}</p>
        </div>
        {queueLength > 1 && (
          <span className="shrink-0 text-[10px] text-fg-secondary">
            1 of {queueLength}
          </span>
        )}
      </div>

      {/* Body — focal picker | form */}
      <div className="flex flex-1 gap-0 overflow-hidden">

        {/* Left: focal point picker */}
        <div className="w-[260px] shrink-0 overflow-y-auto border-r border-line p-4">
          <FocalPointPicker
            imageUrl={staging.localUrl}
            value={focalPoint}
            onChange={setFocalPoint}
          />
        </div>

        {/* Right: metadata fields */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4">

          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
              Classification <span className="text-red-500">*</span>
            </label>
            <CmtWrapper value={cmt} onChange={setCmt} />
          </div>

          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
              Alt Text
            </label>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Describe the image for accessibility"
              className="w-full rounded border border-line bg-canvas px-3 py-1.5 text-sm text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
            />
          </div>

          <div className="space-y-2">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                Caption
              </label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={2}
                placeholder="Caption text…"
                className="w-full resize-none rounded border border-line bg-canvas px-3 py-1.5 text-sm text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
              />
            </div>
            {caption.length > 0 && (
              <>
                <div>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                    Caption Classification
                  </p>
                  <CmtWrapper value={captionCmt} onChange={setCaptionCmt} />
                </div>
                <div className="rounded border border-line bg-fill px-3 py-2 text-xs text-fg">
                  <PortionMark classification={captionCmt.classification} disseminationControls={captionCmt.disseminationControls} />
                  {caption}
                </div>
              </>
            )}
          </div>

          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
              Credit
            </label>
            <input
              type="text"
              value={credit}
              onChange={(e) => setCredit(e.target.value)}
              placeholder="Source or attribution"
              className="w-full rounded border border-line bg-canvas px-3 py-1.5 text-sm text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
            />
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-line px-5 py-3">
        <label className="flex cursor-pointer items-center gap-2 text-[11px] text-fg-secondary">
          <input
            type="checkbox"
            checked={setAsThumbnail}
            onChange={e => setSetAsThumbnail(e.target.checked)}
            className="accent-accent-primary-solid"
          />
          Set as article thumbnail
        </label>
        <div className="flex items-center gap-2">
          {queueLength > 1 && (
            <button
              type="button"
              onClick={onSkip}
              disabled={uploadPending}
              className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50"
            >
              Skip
            </button>
          )}
          <button
            type="submit"
            disabled={!canUpload}
            className="flex items-center gap-1.5 rounded border border-accent-primary-solid bg-accent-primary-solid px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploadPending ? (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            ) : (
              <UploadCloud size={12} aria-hidden="true" />
            )}
            {uploadPending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </form>
  )
}

// ─── Video staging form ───────────────────────────────────────────────────────

type ThumbnailSource = 'none' | 'frame' | 'file' | 'existing'

interface VideoUploadMeta {
  classification: string
  disseminationControls: string[]
  altText: string
  caption: string
  captionClassification: string
  captionDisseminationControls: string[]
  credit: string
  duration?: number
  width?: number
  height?: number
  /** Mutually exclusive thumbnail sources — at most one will be set. */
  thumbnailArtifactId?: string  // pick from existing image artifacts
  thumbnailBlob?: Blob          // frame captured from the video
  thumbnailFile?: File          // user-uploaded image file
}

function VideoStagingForm({
  staging,
  queueLength,
  uploadPending,
  onUpload,
  onSkip,
  onCancel,
  imageArtifacts,
}: {
  staging: StagingFile
  queueLength: number
  uploadPending: boolean
  onUpload: (meta: VideoUploadMeta) => void
  onSkip: () => void
  onCancel: () => void
  imageArtifacts: WireImageArtifact[]
}) {
  const [cmt, setCmt] = useState<CmtResult | undefined>(undefined)
  const [altText, setAltText] = useState('')
  const [caption, setCaption] = useState('')
  const [captionCmt, setCaptionCmt] = useState<CmtResult>({ classification: 'U', disseminationControls: [] })
  const [credit, setCredit] = useState('')
  const [videoDuration, setVideoDuration] = useState<number | undefined>()
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | undefined>()

  // Thumbnail state
  const [thumbSource, setThumbSource] = useState<ThumbnailSource>('none')
  const [capturedFrame, setCapturedFrame] = useState<{ blob: Blob; previewUrl: string } | null>(null)
  const [thumbFile, setThumbFile] = useState<{ file: File; previewUrl: string } | null>(null)
  const [thumbArtifactId, setThumbArtifactId] = useState<string>('')

  const videoRef  = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Revoke object URLs on unmount to prevent memory leaks.
  useEffect(() => {
    return () => {
      if (capturedFrame) URL.revokeObjectURL(capturedFrame.previewUrl)
      if (thumbFile)     URL.revokeObjectURL(thumbFile.previewUrl)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function captureCurrentFrame() {
    const video  = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || video.readyState < 2) return

    canvas.width  = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)

    canvas.toBlob(blob => {
      if (!blob) return
      setCapturedFrame(prev => {
        if (prev) URL.revokeObjectURL(prev.previewUrl)
        return { blob, previewUrl: URL.createObjectURL(blob) }
      })
    }, 'image/jpeg', 0.92)
  }

  function handleThumbFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setThumbFile(prev => {
      if (prev) URL.revokeObjectURL(prev.previewUrl)
      return { file, previewUrl: URL.createObjectURL(file) }
    })
    // Reset the input so the same file can be re-selected after a clear.
    e.target.value = ''
  }

  function changeThumbSource(next: ThumbnailSource) {
    setThumbSource(next)
    // Clear state for sources we're leaving so we don't carry stale data.
    if (next !== 'frame') {
      setCapturedFrame(prev => { if (prev) URL.revokeObjectURL(prev.previewUrl); return null })
    }
    if (next !== 'file') {
      setThumbFile(prev => { if (prev) URL.revokeObjectURL(prev.previewUrl); return null })
    }
    if (next !== 'existing') setThumbArtifactId('')
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!cmt || uploadPending) return
    onUpload({
      classification:               cmt.classification,
      disseminationControls:        cmt.disseminationControls,
      altText,
      caption,
      captionClassification:        captionCmt.classification,
      captionDisseminationControls: captionCmt.disseminationControls,
      credit,
      duration:             videoDuration,
      width:                videoDimensions?.width,
      height:               videoDimensions?.height,
      thumbnailArtifactId:  thumbSource === 'existing' && thumbArtifactId ? thumbArtifactId : undefined,
      thumbnailBlob:        thumbSource === 'frame'    ? capturedFrame?.blob  : undefined,
      thumbnailFile:        thumbSource === 'file'     ? thumbFile?.file      : undefined,
    })
  }

  const thumbSourceOptions: { value: ThumbnailSource; label: string }[] = [
    { value: 'none',     label: 'None'          },
    { value: 'frame',    label: 'Capture frame' },
    { value: 'file',     label: 'Upload image'  },
    ...(imageArtifacts.length > 0
      ? [{ value: 'existing' as ThumbnailSource, label: 'Existing image' }]
      : []),
  ]

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      {/* Sub-header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-line bg-fill px-5 py-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploadPending}
          className="rounded p-1 text-fg-secondary transition-colors hover:bg-canvas hover:text-fg disabled:opacity-40"
          aria-label="Cancel upload"
        >
          <ArrowLeft size={14} />
        </button>
        <VideoIcon size={14} className="shrink-0 text-fg-secondary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-fg" title={staging.file.name}>
            {staging.file.name}
          </p>
          <p className="text-[10px] text-fg-secondary">{fmtBytes(staging.file.size)} · video</p>
        </div>
        {queueLength > 1 && (
          <span className="shrink-0 text-[10px] text-fg-secondary">1 of {queueLength}</span>
        )}
      </div>

      {/* Preview — seekable so the user can position for frame capture */}
      <div className="shrink-0 border-b border-line bg-black">
        <video
          ref={videoRef}
          src={staging.localUrl}
          controls
          muted
          playsInline
          onLoadedMetadata={e => {
            const v = e.currentTarget
            setVideoDuration(Math.round(v.duration * 1000))
            setVideoDimensions({ width: v.videoWidth, height: v.videoHeight })
          }}
          className="mx-auto max-h-40 w-full object-contain"
        />
      </div>

      {/* Hidden canvas used only for frame capture */}
      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

      {/* Fields */}
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        <div>
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Classification <span className="text-red-500">*</span>
          </label>
          <CmtWrapper value={cmt} onChange={setCmt} />
        </div>

        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Alt Text
          </label>
          <input
            type="text"
            value={altText}
            onChange={e => setAltText(e.target.value)}
            placeholder="Describe the video for accessibility"
            className="w-full rounded border border-line bg-canvas px-3 py-1.5 text-sm text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
          />
        </div>

        <div className="space-y-2">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
              Caption
            </label>
            <textarea
              value={caption}
              onChange={e => setCaption(e.target.value)}
              rows={2}
              placeholder="Caption text…"
              className="w-full resize-none rounded border border-line bg-canvas px-3 py-1.5 text-sm text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
            />
          </div>
          {caption.length > 0 && (
            <div>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                Caption Classification
              </p>
              <CmtWrapper value={captionCmt} onChange={setCaptionCmt} />
            </div>
          )}
        </div>

        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Credit
          </label>
          <input
            type="text"
            value={credit}
            onChange={e => setCredit(e.target.value)}
            placeholder="Source or attribution"
            className="w-full rounded border border-line bg-canvas px-3 py-1.5 text-sm text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
          />
        </div>

        {/* ── Thumbnail ── */}
        <div>
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Article Thumbnail
          </p>

          {/* Source selector */}
          <div className="mb-3 flex flex-wrap gap-1">
            {thumbSourceOptions.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => changeThumbSource(value)}
                className={`rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                  thumbSource === value
                    ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                    : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Capture frame */}
          {thumbSource === 'frame' && (
            <div className="space-y-2">
              <p className="text-[11px] text-fg-secondary">
                Seek to the desired frame using the video controls above, then capture it.
              </p>
              <button
                type="button"
                onClick={captureCurrentFrame}
                className="rounded border border-line bg-canvas px-3 py-1.5 text-[11px] font-semibold text-fg transition-colors hover:bg-fill"
              >
                Capture current frame
              </button>
              {capturedFrame && (
                <img
                  src={capturedFrame.previewUrl}
                  alt="Captured thumbnail preview"
                  className="h-40 w-full rounded border border-line object-cover"
                />
              )}
            </div>
          )}

          {/* Upload image */}
          {thumbSource === 'file' && (
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleThumbFileChange}
                className="hidden"
                aria-label="Upload thumbnail image"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full rounded border border-dashed border-line bg-canvas px-3 py-2 text-[11px] text-fg-secondary transition-colors hover:bg-fill"
              >
                {thumbFile ? thumbFile.file.name : 'Click to choose an image…'}
              </button>
              {thumbFile && (
                <img
                  src={thumbFile.previewUrl}
                  alt="Thumbnail preview"
                  className="h-32 w-full rounded border border-line object-cover"
                />
              )}
            </div>
          )}

          {/* Existing image artifact */}
          {thumbSource === 'existing' && (
            <div className="space-y-2">
              <select
                value={thumbArtifactId}
                onChange={e => setThumbArtifactId(e.target.value)}
                className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-[11px] text-fg focus:border-accent-primary-solid focus:outline-none"
              >
                <option value="">— select an image —</option>
                {imageArtifacts.map(a => (
                  <option key={a.id} value={a.id}>{a.filename}</option>
                ))}
              </select>
              {thumbArtifactId && (() => {
                const a = imageArtifacts.find(x => x.id === thumbArtifactId)
                return a ? (
                  <img
                    src={a.presignedUrl}
                    alt={a.altText ?? a.filename}
                    className="h-32 w-full rounded border border-line object-cover"
                  />
                ) : null
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-3">
        {queueLength > 1 && (
          <button
            type="button"
            onClick={onSkip}
            disabled={uploadPending}
            className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50"
          >
            Skip
          </button>
        )}
        <button
          type="submit"
          disabled={!cmt || uploadPending}
          className="flex items-center gap-1.5 rounded border border-accent-primary-solid bg-accent-primary-solid px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploadPending ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <UploadCloud size={12} aria-hidden="true" />
          )}
          {uploadPending ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  )
}

// ─── Captions staging form ───────────────────────────────────────────────────

function CaptionsStagingForm({
  staging,
  queueLength,
  uploadPending,
  onUpload,
  onSkip,
  onCancel,
}: {
  staging: StagingFile
  queueLength: number
  uploadPending: boolean
  onUpload: (classification: string, disseminationControls?: string[]) => void
  onSkip: () => void
  onCancel: () => void
}) {
  const [cmt, setCmt] = useState<CmtResult | undefined>(undefined)
  const canUpload = !uploadPending && cmt !== undefined

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!cmt || !canUpload) return
    onUpload(cmt.classification, cmt.disseminationControls.length > 0 ? cmt.disseminationControls : undefined)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center gap-2 border-b border-line bg-fill px-5 py-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploadPending}
          className="rounded p-1 text-fg-secondary transition-colors hover:bg-canvas hover:text-fg disabled:opacity-40"
          aria-label="Cancel upload"
        >
          <ArrowLeft size={14} />
        </button>
        <CaptionsIcon size={14} className="shrink-0 text-fg-secondary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-fg" title={staging.file.name}>
            {staging.file.name}
          </p>
          <p className="text-[10px] text-fg-secondary">{fmtBytes(staging.file.size)} · captions file</p>
        </div>
        {queueLength > 1 && (
          <span className="shrink-0 text-[10px] text-fg-secondary">1 of {queueLength}</span>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        <div>
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Classification <span className="text-red-500">*</span>
          </label>
          <CmtWrapper value={cmt} onChange={setCmt} />
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-3">
        {queueLength > 1 && (
          <button
            type="button"
            onClick={onSkip}
            disabled={uploadPending}
            className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50"
          >
            Skip
          </button>
        )}
        <button
          type="submit"
          disabled={!canUpload}
          className="flex items-center gap-1.5 rounded border border-accent-primary-solid bg-accent-primary-solid px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploadPending ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <UploadCloud size={12} aria-hidden="true" />
          )}
          {uploadPending ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  )
}

// ─── Audio staging form ──────────────────────────────────────────────────────

function AudioStagingForm({
  staging,
  queueLength,
  uploadPending,
  onUpload,
  onSkip,
  onCancel,
}: {
  staging: StagingFile
  queueLength: number
  uploadPending: boolean
  onUpload: (classification: string, disseminationControls?: string[]) => void
  onSkip: () => void
  onCancel: () => void
}) {
  const [cmt, setCmt] = useState<CmtResult | undefined>(undefined)
  const canUpload = !uploadPending && cmt !== undefined

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!cmt || !canUpload) return
    onUpload(cmt.classification, cmt.disseminationControls.length > 0 ? cmt.disseminationControls : undefined)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center gap-2 border-b border-line bg-fill px-5 py-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploadPending}
          className="rounded p-1 text-fg-secondary transition-colors hover:bg-canvas hover:text-fg disabled:opacity-40"
          aria-label="Cancel upload"
        >
          <ArrowLeft size={14} />
        </button>
        <Volume2 size={14} className="shrink-0 text-fg-secondary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-fg" title={staging.file.name}>
            {staging.file.name}
          </p>
          <p className="text-[10px] text-fg-secondary">{fmtBytes(staging.file.size)} · audio file</p>
        </div>
        {queueLength > 1 && (
          <span className="shrink-0 text-[10px] text-fg-secondary">1 of {queueLength}</span>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        <div>
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Classification <span className="text-red-500">*</span>
          </label>
          <CmtWrapper value={cmt} onChange={setCmt} />
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-3">
        {queueLength > 1 && (
          <button
            type="button"
            onClick={onSkip}
            disabled={uploadPending}
            className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50"
          >
            Skip
          </button>
        )}
        <button
          type="submit"
          disabled={!canUpload}
          className="flex items-center gap-1.5 rounded border border-accent-primary-solid bg-accent-primary-solid px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploadPending ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <UploadCloud size={12} aria-hidden="true" />
          )}
          {uploadPending ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  )
}

// ─── Visual staging form ──────────────────────────────────────────────────────

function VisualStagingForm({
  staging,
  queueLength,
  uploadPending,
  onUpload,
  onSkip,
  onCancel,
}: {
  staging: StagingFile
  queueLength: number
  uploadPending: boolean
  onUpload: (classification: string, disseminationControls?: string[]) => void
  onSkip: () => void
  onCancel: () => void
}) {
  const [cmt, setCmt] = useState<CmtResult | undefined>(undefined)

  const canUpload = !uploadPending && cmt !== undefined

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!cmt || !canUpload) return
    onUpload(cmt.classification, cmt.disseminationControls.length > 0 ? cmt.disseminationControls : undefined)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      {/* Sub-header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-line bg-fill px-5 py-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploadPending}
          className="rounded p-1 text-fg-secondary transition-colors hover:bg-canvas hover:text-fg disabled:opacity-40"
          aria-label="Cancel upload"
        >
          <ArrowLeft size={14} />
        </button>
        <Package size={14} className="shrink-0 text-fg-secondary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-fg" title={staging.file.name}>
            {staging.file.name}
          </p>
          <p className="text-[10px] text-fg-secondary">{fmtBytes(staging.file.size)} · .visual package</p>
        </div>
        {queueLength > 1 && (
          <span className="shrink-0 text-[10px] text-fg-secondary">
            1 of {queueLength}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        <div>
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Classification <span className="text-red-500">*</span>
          </label>
          <CmtWrapper value={cmt} onChange={setCmt} />
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-3">
        {queueLength > 1 && (
          <button
            type="button"
            onClick={onSkip}
            disabled={uploadPending}
            className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50"
          >
            Skip
          </button>
        )}
        <button
          type="submit"
          disabled={!canUpload}
          className="flex items-center gap-1.5 rounded border border-accent-primary-solid bg-accent-primary-solid px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploadPending ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <UploadCloud size={12} aria-hidden="true" />
          )}
          {uploadPending ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  )
}

// ─── Drop zone ────────────────────────────────────────────────────────────────

function DropZone({ onFiles }: { onFiles: (files: FileList) => void }) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setDragging(true)
  }

  function handleDragLeave() {
    setDragging(false)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files)
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      onFiles(e.target.files)
      e.target.value = ''
    }
  }

  return (
    <div
      className={`mx-5 mb-5 flex cursor-pointer items-center gap-3 rounded border-2 border-dashed px-4 py-3 transition-colors ${
        dragging
          ? 'border-accent-primary-solid bg-accent-primary-subtle'
          : 'border-line bg-canvas hover:border-line-strong'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      aria-label="Drop images, videos, audio, captions, or .visual packages here or click to browse"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
      }}
    >
      <UploadCloud
        size={18}
        className={dragging ? 'text-accent-primary-fg' : 'text-fg-secondary'}
        aria-hidden="true"
      />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-fg">
          {dragging ? 'Drop to add files' : 'Add images, videos, audio, captions, or visuals'}
        </p>
        <p className="text-[10px] text-fg-secondary">
          JPEG, PNG, WebP, GIF · MP4, WebM, MOV · MP3, WAV, AAC, OGG · VTT, SRT — or .visual packages
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*,audio/*,.visual,.vtt,.srt,.webvtt"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleInputChange}
      />
    </div>
  )
}

// ─── Sub-components (shared) ──────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
      {children}
    </h2>
  )
}

function MetaChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-secondary">
        {label}
      </span>
      <span className="text-[11px] text-fg">{value}</span>
    </span>
  )
}

function DownloadButton({ href, filename }: { href: string; filename: string }) {
  return (
    <a
      href={href}
      download={filename}
      target="_blank"
      rel="noreferrer"
      className="shrink-0 rounded p-1.5 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
      title={`Download ${filename}`}
      aria-label={`Download ${filename}`}
    >
      <Download size={13} aria-hidden="true" />
    </a>
  )
}

// ─── Image artifact card ──────────────────────────────────────────────────────

function ImageArtifactCard({
  artifact,
  onDelete,
  readOnly,
}: {
  artifact: WireImageArtifact
  onDelete: (id: string) => void
  readOnly?: boolean
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  if (confirmingDelete) {
    return (
      <div className="flex flex-col overflow-hidden rounded border border-error-line bg-error-fill">
        <div className="relative h-20 overflow-hidden opacity-40">
          <img
            src={artifact.presignedUrl}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col gap-2 p-3">
          <p className="text-xs font-semibold text-error-fg">Delete this image?</p>
          <p className="truncate text-[11px] text-error-fg/70" title={artifact.filename}>
            {artifact.filename}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="flex-1 rounded border border-line bg-canvas px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onDelete(artifact.id)}
              className="flex-1 rounded border border-error-line bg-error-fill px-2 py-1 text-[11px] font-semibold text-error-fg transition-colors hover:opacity-80"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded border border-line bg-canvas">
      <a
        href={artifact.presignedUrl}
        target="_blank"
        rel="noreferrer"
        className="block overflow-hidden"
        tabIndex={-1}
        aria-hidden="true"
      >
        <img
          src={artifact.presignedUrl}
          alt={artifact.altText ?? artifact.filename}
          className="h-36 w-full object-cover transition-opacity hover:opacity-90"
        />
      </a>

      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <p
            className="min-w-0 truncate text-xs font-semibold text-fg"
            title={artifact.filename}
          >
            {artifact.filename}
          </p>
          <div className="flex shrink-0 items-center gap-0.5">
            <DownloadButton href={artifact.presignedUrl} filename={artifact.filename} />
            {!readOnly && (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="rounded p-1.5 text-fg-secondary transition-colors hover:bg-error-fill hover:text-error-fg"
                aria-label={`Delete ${artifact.filename}`}
              >
                <Trash2 size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-0.5 font-mono text-[10px] text-fg-secondary">
          <PortionMark classification={artifact.classification} disseminationControls={artifact.disseminationControls} />
        </p>

        {artifact.altText && (
          <p className="mt-1.5 text-[11px] text-fg" title={artifact.altText}>
            {artifact.altText}
          </p>
        )}

        {artifact.caption && (
          <p className="mt-0.5 text-[11px] italic text-fg-secondary line-clamp-2">
            {artifact.caption}
          </p>
        )}

        {artifact.credit && (
          <p className="mt-0.5 text-[10px] text-fg-secondary">Credit: {artifact.credit}</p>
        )}

        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 border-t border-line pt-2">
          <MetaChip label="Size" value={fmtBytes(artifact.fileSize)} />
          <MetaChip label="By" value={artifact.uploadedBy} />
          <MetaChip label="On" value={fmtDate(artifact.uploadedAt)} />
        </div>
      </div>
    </div>
  )
}

// ─── Video artifact card ──────────────────────────────────────────────────────

function VideoArtifactCard({
  artifact,
  thumbnailArtifact,
  onDelete,
  readOnly,
}: {
  artifact: WireVideoArtifact
  thumbnailArtifact?: WireImageArtifact
  onDelete: (id: string) => void
  readOnly?: boolean
}) {
  const [playing, setPlaying] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  if (confirmingDelete) {
    return (
      <div className="flex flex-col overflow-hidden rounded border border-error-line bg-error-fill">
        <div className="relative flex h-20 items-center justify-center bg-black/40 opacity-40">
          <VideoIcon size={24} className="text-white/60" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-2 p-3">
          <p className="text-xs font-semibold text-error-fg">Delete this video?</p>
          <p className="truncate text-[11px] text-error-fg/70" title={artifact.filename}>
            {artifact.filename}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="flex-1 rounded border border-line bg-canvas px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onDelete(artifact.id)}
              className="flex-1 rounded border border-error-line bg-error-fill px-2 py-1 text-[11px] font-semibold text-error-fg transition-colors hover:opacity-80"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded border border-line bg-canvas">
      {/* Thumbnail / player — clicking transitions from poster to active player */}
      <div className="relative bg-black">
        {playing ? (
          <video
            src={artifact.presignedUrl}
            controls
            autoPlay
            playsInline
            className="w-full"
            onEnded={() => setPlaying(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group relative flex h-36 w-full items-center justify-center overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary-solid"
            aria-label={`Play ${artifact.filename}`}
          >
            {thumbnailArtifact ? (
              <img
                src={thumbnailArtifact.presignedUrl}
                alt={thumbnailArtifact.altText ?? ''}
                className="h-full w-full object-cover opacity-70 transition-opacity group-hover:opacity-90"
                aria-hidden="true"
              />
            ) : (
              <video
                src={artifact.presignedUrl}
                muted
                playsInline
                preload="metadata"
                className="h-full w-full object-cover opacity-70 transition-opacity group-hover:opacity-90"
                aria-hidden="true"
              />
            )}
            {/* Play button overlay */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 shadow-lg transition-transform group-hover:scale-110">
                <svg viewBox="0 0 24 24" fill="white" className="h-5 w-5 translate-x-0.5" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </button>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-xs font-semibold text-fg" title={artifact.filename}>
            {artifact.filename}
          </p>
          <div className="flex shrink-0 items-center gap-0.5">
            <DownloadButton href={artifact.presignedUrl} filename={artifact.filename} />
            {!readOnly && (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="rounded p-1.5 text-fg-secondary transition-colors hover:bg-error-fill hover:text-error-fg"
                aria-label={`Delete ${artifact.filename}`}
              >
                <Trash2 size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-0.5 font-mono text-[10px] text-fg-secondary">
          <PortionMark classification={artifact.classification} disseminationControls={artifact.disseminationControls} />
        </p>

        {artifact.caption && (
          <p className="mt-1 text-[11px] italic text-fg-secondary line-clamp-2">{artifact.caption}</p>
        )}
        {artifact.credit && (
          <p className="mt-0.5 text-[10px] text-fg-secondary">Credit: {artifact.credit}</p>
        )}

        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 border-t border-line pt-2">
          <MetaChip label="Size" value={fmtBytes(artifact.fileSize)} />
          {artifact.duration !== undefined && (
            <MetaChip
              label="Duration"
              value={`${Math.floor(artifact.duration / 60000)}:${String(Math.floor((artifact.duration % 60000) / 1000)).padStart(2, '0')}`}
            />
          )}
          <MetaChip label="By" value={artifact.uploadedBy} />
          <MetaChip label="On" value={fmtDate(artifact.uploadedAt)} />
        </div>
      </div>
    </div>
  )
}

// ─── File artifact row ────────────────────────────────────────────────────────

function FileArtifactRow({
  artifact,
  note,
  onDelete,
  readOnly,
}: {
  artifact: WireWordDocumentArtifact | WireVisualArtifact | WireCaptionsArtifact | WireAudioArtifact
  note?: string
  onDelete: (id: string) => void
  readOnly?: boolean
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const Icon = artifact.type === 'word-document'
    ? FileText
    : artifact.type === 'visual'
      ? Package
      : artifact.type === 'captions'
        ? CaptionsIcon
        : Volume2

  if (confirmingDelete) {
    return (
      <div className="flex items-start gap-3 rounded border border-error-line bg-error-fill p-3">
        <Icon size={16} className="mt-0.5 shrink-0 text-error-fg/60" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-error-fg">Delete this artifact?</p>
          <p className="mt-0.5 truncate text-[11px] text-error-fg/70" title={artifact.filename}>
            {artifact.filename}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="flex-1 rounded border border-line bg-canvas px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onDelete(artifact.id)}
              className="flex-1 rounded border border-error-line bg-error-fill px-2 py-1 text-[11px] font-semibold text-error-fg transition-colors hover:opacity-80"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3 rounded border border-line bg-canvas p-3">
      <div className="mt-0.5 shrink-0 text-fg-secondary">
        <Icon size={16} aria-hidden="true" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className="min-w-0 truncate text-xs font-semibold text-fg"
            title={artifact.filename}
          >
            {artifact.filename}
          </p>
          <div className="flex shrink-0 items-center gap-0.5">
            <DownloadButton href={artifact.presignedUrl} filename={artifact.filename} />
            {!readOnly && (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="rounded p-1.5 text-fg-secondary transition-colors hover:bg-error-fill hover:text-error-fg"
                aria-label={`Delete ${artifact.filename}`}
              >
                <Trash2 size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-0.5 font-mono text-[10px] text-fg-secondary">
          <PortionMark classification={artifact.classification} disseminationControls={artifact.disseminationControls} />
        </p>

        {note && <p className="mt-1 text-[11px] italic text-fg-secondary">{note}</p>}

        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <MetaChip label="Size" value={fmtBytes(artifact.fileSize)} />
          <MetaChip label="By" value={artifact.uploadedBy} />
          <MetaChip label="On" value={fmtDate(artifact.uploadedAt)} />
        </div>
      </div>
    </div>
  )
}

// ─── ArticleArtifactsDrawer ───────────────────────────────────────────────────

interface Props {
  article: WireArticleFull
  open: boolean
  onClose: () => void
  onAddArtifact: (artifact: WireArtifact) => void
  onRemoveArtifact: (artifactId: string) => void
  onSetThumbnail: (thumbnail: WireArticleThumbnail) => void
  readOnly?: boolean
}

export function ArticleArtifactsDrawer({
  article,
  open,
  onClose,
  onAddArtifact,
  onRemoveArtifact,
  onSetThumbnail,
  readOnly,
}: Props) {
  const { artifactService } = useServices()

  const [stagingQueue, setStagingQueue] = useState<StagingFile[]>([])
  const [uploadPending, setUploadPending] = useState(false)

  const currentStaging = stagingQueue[0] ?? null
  const isStaging = currentStaging !== null

  const handleFiles = useCallback(
    (fileList: FileList) => {
      const imageFiles = Array.from(fileList).filter((f) => f.type.startsWith('image/'))
      const videoFiles = Array.from(fileList).filter((f) => f.type.startsWith('video/'))
      const audioFiles = Array.from(fileList).filter((f) => f.type.startsWith('audio/'))
      const visualFiles = Array.from(fileList).filter((f) =>
        f.name.toLowerCase().endsWith('.visual'),
      )
      const captionsFiles = Array.from(fileList).filter((f) =>
        /\.(vtt|srt|webvtt)$/i.test(f.name),
      )
      const accepted = [
        ...imageFiles.map((f): StagingFile => ({ file: f, localUrl: URL.createObjectURL(f), type: 'image' })),
        ...videoFiles.map((f): StagingFile => ({ file: f, localUrl: URL.createObjectURL(f), type: 'video' })),
        ...audioFiles.map((f): StagingFile => ({ file: f, localUrl: '', type: 'audio' })),
        ...visualFiles.map((f): StagingFile => ({ file: f, localUrl: '', type: 'visual' })),
        ...captionsFiles.map((f): StagingFile => ({ file: f, localUrl: '', type: 'captions' })),
      ]
      if (accepted.length === 0) {
        toast.error('Unsupported file type.', { description: 'Drop images (JPEG, PNG, WebP, GIF), videos (MP4, WebM, MOV), audio (MP3, WAV, AAC, OGG), captions (.vtt, .srt), or .visual packages.' })
        return
      }
      setStagingQueue(accepted)
    },
    [],
  )

  function advanceQueue() {
    setStagingQueue((prev) => {
      if (prev[0].localUrl) URL.revokeObjectURL(prev[0].localUrl)
      return prev.slice(1)
    })
  }

  function cancelStaging() {
    stagingQueue.forEach((f) => { if (f.localUrl) URL.revokeObjectURL(f.localUrl) })
    setStagingQueue([])
  }

  async function handleUpload(meta: ImageUploadMeta) {
    if (!currentStaging) return
    setUploadPending(true)
    try {
      const artifact = await artifactService.upload(
        article.id,
        currentStaging.file,
        'image',
        currentStaging.file.type || 'image/jpeg',
        meta.classification,
        meta.disseminationControls.length > 0 ? meta.disseminationControls : undefined,
        {
          altText:                      meta.altText.trim()   || undefined,
          caption:                      meta.caption.trim()   || undefined,
          captionClassification:        meta.caption.trim() ? meta.captionClassification : undefined,
          captionDisseminationControls: meta.caption.trim() && meta.captionDisseminationControls.length > 0
            ? meta.captionDisseminationControls
            : undefined,
          credit:     meta.credit.trim() || undefined,
          focalPoint: meta.focalPoint,
        },
      )
      onAddArtifact(artifact)
      if (meta.setAsThumbnail && artifact.type === 'image') {
        const a = artifact as WireImageArtifact
        onSetThumbnail({
          artifactId: a.id, altText: a.altText, caption: a.caption,
          captionClassification: a.captionClassification, captionDisseminationControls: a.captionDisseminationControls,
          classification: a.classification, disseminationControls: a.disseminationControls,
          credit: a.credit, focalPoint: a.focalPoint, presignedUrl: a.presignedUrl, s3Key: a.s3Key,
        })
      }
      toast.success('Image uploaded', { description: currentStaging.file.name })
      advanceQueue()
    } catch {
      toast.error('Upload failed — please try again.')
    } finally {
      setUploadPending(false)
    }
  }

  async function handleCaptionsUpload(classification: string, disseminationControls?: string[]) {
    if (!currentStaging) return
    setUploadPending(true)
    try {
      const artifact = await artifactService.upload(
        article.id,
        currentStaging.file,
        'captions',
        'text/vtt',
        classification,
        disseminationControls,
      )
      onAddArtifact(artifact)
      toast.success('Captions file uploaded', { description: currentStaging.file.name })
      advanceQueue()
    } catch {
      toast.error('Upload failed — please try again.')
    } finally {
      setUploadPending(false)
    }
  }

  async function handleVisualUpload(classification: string, disseminationControls?: string[]) {
    if (!currentStaging) return
    setUploadPending(true)
    try {
      const artifact = await artifactService.upload(
        article.id,
        currentStaging.file,
        'visual',
        'application/zip',
        classification,
        disseminationControls,
      )
      onAddArtifact(artifact)
      toast.success('Visual package uploaded', { description: currentStaging.file.name })
      advanceQueue()
    } catch {
      toast.error('Upload failed — please try again.')
    } finally {
      setUploadPending(false)
    }
  }

  async function handleAudioUpload(classification: string, disseminationControls?: string[]) {
    if (!currentStaging) return
    setUploadPending(true)
    try {
      const artifact = await artifactService.upload(
        article.id,
        currentStaging.file,
        'audio',
        currentStaging.file.type || 'audio/mpeg',
        classification,
        disseminationControls,
      )
      onAddArtifact(artifact)
      toast.success('Audio uploaded', { description: currentStaging.file.name })
      advanceQueue()
    } catch {
      toast.error('Upload failed — please try again.')
    } finally {
      setUploadPending(false)
    }
  }

  async function handleVideoUpload(meta: VideoUploadMeta) {
    if (!currentStaging) return
    setUploadPending(true)
    try {
      // Upload thumbnail first so its ID can be embedded in the video artifact.
      let resolvedThumbnailId = meta.thumbnailArtifactId
      let thumbnailArtifact: WireImageArtifact | undefined

      if (meta.thumbnailBlob || meta.thumbnailFile) {
        const baseName = currentStaging.file.name.replace(/\.[^.]+$/, '') ?? 'video'
        const file = meta.thumbnailFile
          ?? new File([meta.thumbnailBlob!], `thumbnail-${baseName}.jpg`, { type: 'image/jpeg' })
        const uploaded = await artifactService.upload(
          article.id, file, 'image', file.type,
          meta.classification,
          meta.disseminationControls.length > 0 ? meta.disseminationControls : undefined,
        )
        onAddArtifact(uploaded)
        if (uploaded.type === 'image') {
          thumbnailArtifact = uploaded as WireImageArtifact
          resolvedThumbnailId = thumbnailArtifact.id
        }
      } else if (meta.thumbnailArtifactId) {
        thumbnailArtifact = (article.artifacts ?? []).find(
          (x): x is WireImageArtifact => x.type === 'image' && x.id === meta.thumbnailArtifactId,
        )
      }

      // Upload the video with thumbnailArtifactId embedded so the two are linked.
      const artifact = await artifactService.upload(
        article.id,
        currentStaging.file,
        'video',
        currentStaging.file.type || 'video/mp4',
        meta.classification,
        meta.disseminationControls.length > 0 ? meta.disseminationControls : undefined,
        {
          altText:                      meta.altText.trim()   || undefined,
          caption:                      meta.caption.trim()   || undefined,
          captionClassification:        meta.caption.trim() ? meta.captionClassification : undefined,
          captionDisseminationControls: meta.caption.trim() && meta.captionDisseminationControls.length > 0
            ? meta.captionDisseminationControls
            : undefined,
          credit:              meta.credit.trim() || undefined,
          duration:            meta.duration,
          width:               meta.width,
          height:              meta.height,
          thumbnailArtifactId: resolvedThumbnailId,
        },
      )
      onAddArtifact(artifact)

      // Propagate the thumbnail to the article-level thumbnail field.
      if (thumbnailArtifact) {
        const a = thumbnailArtifact
        onSetThumbnail({
          artifactId: a.id, altText: a.altText, caption: a.caption,
          captionClassification: a.captionClassification,
          captionDisseminationControls: a.captionDisseminationControls,
          classification: a.classification, disseminationControls: a.disseminationControls,
          credit: a.credit, focalPoint: a.focalPoint, presignedUrl: a.presignedUrl, s3Key: a.s3Key,
        })
      }

      toast.success('Video uploaded', { description: currentStaging.file.name })
      advanceQueue()
    } catch {
      toast.error('Upload failed — please try again.')
    } finally {
      setUploadPending(false)
    }
  }

  if (!open) return null

  const artifacts = article.artifacts ?? []
  const images   = artifacts.filter((a): a is WireImageArtifact       => a.type === 'image')
  const videos   = artifacts.filter((a): a is WireVideoArtifact       => a.type === 'video')
  const wordDocs = artifacts.filter((a): a is WireWordDocumentArtifact => a.type === 'word-document')
  const visuals  = artifacts.filter((a): a is WireVisualArtifact      => a.type === 'visual')
  const captions = artifacts.filter((a): a is WireCaptionsArtifact    => a.type === 'captions')
  const audios   = artifacts.filter((a): a is WireAudioArtifact       => a.type === 'audio')

  // Images owned by a video as its thumbnail — shown on the video card, not the images grid.
  const videoThumbnailIds = new Set(
    videos.map(v => v.thumbnailArtifactId).filter((id): id is string => id !== undefined),
  )
  const standaloneImages = images.filter(img => !videoThumbnailIds.has(img.id))

  const summaryLine =
    artifacts.length === 0
      ? 'No artifacts attached to this draft'
      : [
          images.length   > 0 ? pl(images.length,   'image')           : null,
          videos.length   > 0 ? pl(videos.length,   'video')           : null,
          wordDocs.length > 0 ? pl(wordDocs.length,  'source document') : null,
          visuals.length  > 0 ? pl(visuals.length,   'visual package')  : null,
          captions.length > 0 ? pl(captions.length,  'captions file')   : null,
          audios.length   > 0 ? pl(audios.length,    'audio file')      : null,
        ]
          .filter(Boolean)
          .join(' · ')

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-30 bg-black/30"
        onClick={isStaging ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Article artifacts"
        className="fixed right-0 top-[3rem] z-40 flex h-[calc(100vh-3rem)] w-[640px] flex-col bg-surface shadow-2xl"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-4">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
              Artifacts
            </p>
            <p
              className="mt-0.5 truncate text-sm font-semibold text-fg"
              title={article.title}
            >
              {article.title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={uploadPending}
            className="ml-4 shrink-0 rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-40"
            aria-label="Close artifacts panel"
          >
            <X size={16} />
          </button>
        </div>

        {/* Count sub-header (list view only) */}
        {!isStaging && (
          <div className="shrink-0 border-b border-line bg-fill px-6 py-2 text-[11px] text-fg-secondary">
            {summaryLine}
          </div>
        )}

        {/* ── Staging view ── */}
        {isStaging && currentStaging.type === 'captions' && (
          <CaptionsStagingForm
            staging={currentStaging}
            queueLength={stagingQueue.length}
            uploadPending={uploadPending}
            onUpload={(classification, dc) => void handleCaptionsUpload(classification, dc)}
            onSkip={advanceQueue}
            onCancel={cancelStaging}
          />
        )}
        {isStaging && currentStaging.type === 'visual' && (
          <VisualStagingForm
            staging={currentStaging}
            queueLength={stagingQueue.length}
            uploadPending={uploadPending}
            onUpload={(classification) => void handleVisualUpload(classification)}
            onSkip={advanceQueue}
            onCancel={cancelStaging}
          />
        )}
        {isStaging && currentStaging.type === 'audio' && (
          <AudioStagingForm
            staging={currentStaging}
            queueLength={stagingQueue.length}
            uploadPending={uploadPending}
            onUpload={(classification, dc) => void handleAudioUpload(classification, dc)}
            onSkip={advanceQueue}
            onCancel={cancelStaging}
          />
        )}
        {isStaging && currentStaging.type === 'image' && (
          <ImageStagingForm
            staging={currentStaging}
            queueLength={stagingQueue.length}
            uploadPending={uploadPending}
            onUpload={(meta) => void handleUpload(meta)}
            onSkip={advanceQueue}
            onCancel={cancelStaging}
          />
        )}
        {isStaging && currentStaging.type === 'video' && (
          <VideoStagingForm
            staging={currentStaging}
            queueLength={stagingQueue.length}
            uploadPending={uploadPending}
            onUpload={(meta) => void handleVideoUpload(meta)}
            onSkip={advanceQueue}
            onCancel={cancelStaging}
            imageArtifacts={images}
          />
        )}

        {/* ── Artifact list view ── */}
        {!isStaging && (
          <>
            {/* Drop zone — hidden in read-only mode */}
            {!isStaging && !readOnly && (
              <div className="shrink-0 border-b border-line pb-4 pt-4">
                <DropZone onFiles={handleFiles} />
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {artifacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <ImageIcon
                    size={36}
                    className="text-fg-secondary opacity-25"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-fg-secondary">
                    No artifacts attached yet.
                  </p>
                  <p className="text-xs text-fg-secondary opacity-60">
                    Drop an image above to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-10">
                  {standaloneImages.length > 0 && (
                    <section>
                      <SectionHeading>Images ({standaloneImages.length})</SectionHeading>
                      <div className="grid grid-cols-2 gap-3">
                        {standaloneImages.map((a) => (
                          <ImageArtifactCard
                            key={a.id}
                            artifact={a}
                            onDelete={onRemoveArtifact}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {videos.length > 0 && (
                    <section>
                      <SectionHeading>Videos ({videos.length})</SectionHeading>
                      <div className="grid grid-cols-2 gap-3">
                        {videos.map((a) => (
                          <VideoArtifactCard
                            key={a.id}
                            artifact={a}
                            thumbnailArtifact={a.thumbnailArtifactId
                              ? images.find(img => img.id === a.thumbnailArtifactId)
                              : undefined}
                            onDelete={onRemoveArtifact}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {captions.length > 0 && (
                    <section>
                      <SectionHeading>Captions Files ({captions.length})</SectionHeading>
                      <div className="space-y-2">
                        {captions.map((a) => (
                          <FileArtifactRow
                            key={a.id}
                            artifact={a}
                            onDelete={onRemoveArtifact}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {audios.length > 0 && (
                    <section>
                      <SectionHeading>Audio Files ({audios.length})</SectionHeading>
                      <div className="space-y-2">
                        {audios.map((a) => (
                          <FileArtifactRow
                            key={a.id}
                            artifact={a}
                            onDelete={onRemoveArtifact}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {wordDocs.length > 0 && (
                    <section>
                      <SectionHeading>Source Documents ({wordDocs.length})</SectionHeading>
                      <div className="space-y-2">
                        {wordDocs.map((a) => (
                          <FileArtifactRow
                            key={a.id}
                            artifact={a}
                            note={a.revisionNote}
                            onDelete={onRemoveArtifact}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {visuals.length > 0 && (
                    <section>
                      <SectionHeading>Visual Packages ({visuals.length})</SectionHeading>
                      <div className="space-y-2">
                        {visuals.map((a) => (
                          <FileArtifactRow
                            key={a.id}
                            artifact={a}
                            onDelete={onRemoveArtifact}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>


          </>
        )}
      </aside>
    </>
  )
}
